// Skills page (T-350, owner request 2026-11-03): what the third-party day-trader-skill pack can do, and how each skill
// fits Saferfunds. READ-ONLY and STATIC: the data is src/data/dayTraderSkills.js, generated from external/day-trader-skill
// (pinned commit) by scripts/build_skill_catalog.py. Nothing here runs the pack's tools, calls the API or places orders:
// running them from the engine would put unreviewed outside code and Yahoo data on the server (service-separation and
// data-foundation rules). The pack's tools run by hand on the Linux machine (commands shown per skill).
import React, { useMemo, useState } from 'react'
import catalog from '../data/dayTraderSkills'
import { Panel, MONO } from '../components/deskParts'
import { VERDICT, orderSkills, filterSkills, verdictCounts } from '../lib/skillCatalog'

const btn = { ...MONO, fontSize: '0.64rem', letterSpacing: '0.06em', padding: '3px 8px', borderRadius: 4, border: '1px solid #334155', color: '#cbd5e1', background: 'transparent', cursor: 'pointer' }
const label = { ...MONO, fontSize: '0.6rem', letterSpacing: '0.08em', color: '#64748b', textTransform: 'uppercase', marginTop: 8, marginBottom: 2 }
const body = { fontSize: '0.78rem', color: '#cbd5e1', lineHeight: 1.45 }

function Chip({ text, color }) {
  return <span style={{ ...MONO, fontSize: '0.62rem', color, border: `1px solid ${color}`, borderRadius: 3, padding: '1px 6px', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>{text}</span>
}

// One skill card: command, what it does, Saferfunds fit, commands, reads/writes, never-does, full SKILL.md on demand.
function SkillCard({ s, step }) {
  const [open, setOpen] = useState(false)
  const v = VERDICT[s.fit.verdict] || VERDICT.UNREVIEWED
  return (
    <Panel title={`${step}. ${s.command}`} right={<span className="flex gap-2">
      {s.can_lead_to_order && <Chip text="CAN LEAD TO AN ORDER" color="#fbbf24" />}
      <Chip text={v.label} color={v.color} /></span>}>
      <div style={body}>{s.description}</div>
      <div style={label}>Saferfunds fit</div>
      <div style={{ ...body, color: v.color }}>{s.fit.note}</div>
      <div style={label}>Commands (run by hand from external/day-trader-skill)</div>
      <pre style={{ ...MONO, fontSize: '0.68rem', color: '#a5b4fc', whiteSpace: 'pre-wrap', margin: 0 }}>{s.commands.join('\n')}</pre>
      {s.reads_writes && <>
        <div style={label}>What it reads and writes</div>
        <div style={{ ...body, whiteSpace: 'pre-wrap' }}>{s.reads_writes}</div>
      </>}
      <div style={label}>What it never does</div>
      <ul style={{ ...body, margin: 0, paddingLeft: 16, listStyle: 'disc' }}>{s.never_does.map((n, i) => <li key={i}>{n}</li>)}</ul>
      <div className="mt-2"><button style={btn} onClick={() => setOpen(o => !o)}>{open ? 'HIDE' : 'READ'} FULL SKILL.md ({s.sections.length} sections)</button></div>
      {open && <pre style={{ ...MONO, fontSize: '0.68rem', color: '#94a3b8', whiteSpace: 'pre-wrap', marginTop: 6, maxHeight: 480, overflow: 'auto', borderTop: '1px solid #1e293b', paddingTop: 6 }}>{s.skill_md}</pre>}
    </Panel>
  )
}

export default function SkillLab() {
  const [verdict, setVerdict] = useState('ALL')
  const [query, setQuery] = useState('')
  const [showLicense, setShowLicense] = useState(false)
  const ordered = useMemo(() => orderSkills(catalog.skills), [])
  const shown = useMemo(() => filterSkills(ordered, { verdict, query }), [ordered, verdict, query])
  const counts = useMemo(() => verdictCounts(catalog.skills), [])
  const stepOf = (id) => ordered.findIndex(s => s.id === id) + 1

  return (
    <div className="space-y-3 p-3">
      <Panel title="Day Trader skill pack" right={<span>{catalog.source} @ {catalog.commit || 'unknown commit'} · reviewed {catalog.reviewed}</span>}>
        <div style={body}>
          Eleven Claude skills for manual, one-trade-at-a-time day trading on free Yahoo / SEC data. Reference material for
          the next trading family (opening-range breakout, <b>T-350</b>). It is <b>not installed</b> as a skill and <b>not wired
          into the engine</b>; this page only shows what it can do. Pipeline per trade: scanner → news → entry → exit →
          time machine → budget → risk → broker, with alerts while open.
        </div>
        <div style={{ ...body, color: '#fbbf24', marginTop: 6 }}>
          Not evidence: the pack models no slippage, spread or fees, and its Time Machine shuffles bars at random. Any ORB
          claim must come from /new-backtest on our own bars with friction and walk-forward OOS (data gate first).
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-3">
          {['ALL', 'RESEARCH', 'REFERENCE', 'NOT_FOR_ENGINE'].map(k => (
            <button key={k} style={{ ...btn, ...(verdict === k ? { borderColor: (VERDICT[k] || {}).color || '#cbd5e1', color: (VERDICT[k] || {}).color || '#fff' } : {}) }}
              onClick={() => setVerdict(k)}>{k === 'ALL' ? `ALL (${catalog.skills.length})` : `${VERDICT[k].label} (${counts[k] || 0})`}</button>
          ))}
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder="search skills"
            style={{ ...MONO, fontSize: '0.7rem', background: '#0b1220', border: '1px solid #334155', borderRadius: 4, color: '#e2e8f0', padding: '3px 8px' }} />
          <span className="flex-1" />
          <button style={btn} onClick={() => setShowLicense(o => !o)}>{showLicense ? 'HIDE' : 'SHOW'} LICENCE (MIT)</button>
        </div>
        {showLicense && <pre style={{ ...MONO, fontSize: '0.66rem', color: '#94a3b8', whiteSpace: 'pre-wrap', marginTop: 6 }}>{catalog.license}</pre>}
      </Panel>
      {shown.length === 0 && <div style={{ ...body, color: '#64748b' }}>No skill matches.</div>}
      <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(420px, 1fr))' }}>
        {shown.map(s => <SkillCard key={s.id} s={s} step={stepOf(s.id)} />)}
      </div>
    </div>
  )
}
